import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

type Side = 'bottom' | 'top';
type Align = 'start' | 'center' | 'end';

interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The element the popover anchors to. Provide `triggerRef` and call setOpen yourself. */
  triggerRef: React.RefObject<HTMLElement | null>;
  side?: Side;
  align?: Align;
  /** Distance from the trigger in px. */
  sideOffset?: number;
  className?: string;
  children: React.ReactNode;
}

/** Anchored popover: positions itself relative to a trigger element via getBoundingClientRect,
 *  flipping/shifting to stay in viewport. Click-outside and Escape close it. */
export function Popover({
  open,
  onOpenChange,
  triggerRef,
  side = 'bottom',
  align = 'end',
  sideOffset = 6,
  className,
  children,
}: PopoverProps) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  // Close on outside click + Escape.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (contentRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, onOpenChange, triggerRef]);

  // Reset position on close so the next open doesn't paint at the
  // previous open's coordinates for one frame before useLayoutEffect
  // recomputes. Without this, opening the popover after scrolling or
  // navigating made it flash at the prior location — what the user
  // saw as "blinks god knows where on the page".
  React.useEffect(() => {
    if (!open) setPos(null);
  }, [open]);

  // Position whenever open or window resizes.
  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current || !contentRef.current) return;
    const compute = () => {
      const trigger = triggerRef.current!.getBoundingClientRect();
      const content = contentRef.current!.getBoundingClientRect();
      const vpW = window.innerWidth;
      const vpH = window.innerHeight;

      let top = side === 'bottom' ? trigger.bottom + sideOffset : trigger.top - content.height - sideOffset;
      let left =
        align === 'start' ? trigger.left :
        align === 'end' ? trigger.right - content.width :
        trigger.left + (trigger.width - content.width) / 2;

      // Flip vertically if it would overflow.
      if (top + content.height > vpH - 8) top = trigger.top - content.height - sideOffset;
      if (top < 8) top = trigger.bottom + sideOffset;
      top = Math.max(8, Math.min(top, vpH - content.height - 8));

      // Shift horizontally to stay in viewport.
      if (left + content.width > vpW - 8) left = vpW - content.width - 8;
      if (left < 8) left = 8;

      setPos({ top, left });
    };
    compute();
    // Cascading forms grow after opening; keep the newly revealed fields visible.
    const observer = new ResizeObserver(compute);
    observer.observe(contentRef.current);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, side, align, sideOffset, triggerRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={contentRef}
      role="dialog"
      // Pre-measurement: park off-screen with visibility:hidden (still
      // measurable via getBoundingClientRect, but never painted) and
      // hold off on the animate-in class. Once useLayoutEffect computes
      // pos, the next render flips to the real position + starts the
      // entry animation from there. Old behaviour was opacity:0 +
      // animate-in fade-in always-on, which let the fade-in keyframe
      // override the inline opacity for one frame at (-9999,-9999) —
      // not visible itself, but if the layout shift happened just after
      // the first paint the user saw a flash at the prior popover's
      // last location (browser cached scroll/portal position).
      style={pos
        ? { position: 'fixed', top: pos.top, left: pos.left }
        : { position: 'fixed', top: -9999, left: -9999, visibility: 'hidden' }}
      className={cn(
        // `transition-[opacity,transform,filter]` scopes the transition to the
        // entrance properties only. `duration-100` sets `transition-duration`,
        // and CSS's default `transition-property: all` would otherwise animate
        // `top`/`left` too — making the popover visibly *slide* in from the
        // off-screen park position (-9999,-9999) to the anchor coords over the
        // 0.1s duration. Scoping the property list makes position snap
        // instantly while the fade-in/zoom-in entrance still plays (those are
        // CSS @keyframes animations via `animate-in`, unaffected by this).
        'z-[100] min-w-[8rem] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg outline-none transition-[opacity,transform,filter]',
        pos && 'animate-in fade-in zoom-in-95 duration-100',
        className
      )}
    >
      {children}
    </div>,
    document.body
  );
}
